import { RequiredFieldTooltip } from '@/shared/components/required-field-tooltip';
import {
  Field,
  FieldTextarea,
  Tooltip,
} from '@noorden-insurtech/alba-design-system';
import { Info } from 'lucide-react';
import type { TFieldContext } from '../types';

export type TextareaFieldProps = {
  label: string;
  placeholder?: string;
  required?: boolean;
  helperText?: string;
  helperMessage?: string;
  disabled?: boolean;
  maxLength?: number;
  onBlur?: () => void | Promise<void>;
  rows?: number;
};

export function createTextareaField(useFieldContext: () => unknown) {
  return function TextareaField({
    label,
    placeholder,
    required,
    helperText,
    helperMessage,
    disabled,
    maxLength,
    onBlur: customOnBlur,
    rows,
  }: TextareaFieldProps) {
    const field = useFieldContext() as TFieldContext<string>;

    const handleBlur = () => {
      field.handleBlur();
      customOnBlur?.();
    };

    return (
      <RequiredFieldTooltip active={required}>
        <Field.Root invalid={!!field.state.meta.errors.length}>
          <Field.Label>
            {label}
            {required && <span className='text-alba-error-red-500'>*</span>}
            {helperMessage && (
              <Tooltip.Root>
                <Tooltip.Trigger>
                  <Info className='size-4 text-alba-blue-500' />
                </Tooltip.Trigger>
                <Tooltip.Positioner>
                  <Tooltip.Content>
                    <Tooltip.Arrow />
                    {helperMessage}
                  </Tooltip.Content>
                </Tooltip.Positioner>
              </Tooltip.Root>
            )}
          </Field.Label>
          <FieldTextarea
            value={field.state.value || ''}
            onChange={e => field.handleChange(e.target.value)}
            onBlur={handleBlur}
            placeholder={placeholder}
            disabled={disabled}
            maxLength={maxLength}
            rows={rows}
          />
          {helperText && !field.state.meta.errors.length && (
            <Field.HelperText>{helperText}</Field.HelperText>
          )}
          {field.state.meta.errors.length > 0 && (
            <Field.ErrorText>{field.state.meta.errors[0]}</Field.ErrorText>
          )}
        </Field.Root>
      </RequiredFieldTooltip>
    );
  };
}
